import { type FormEvent, useEffect, useId, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { DictationLanguages } from '@/components/account/DictationLanguages';
import { WebSearchDefault } from '@/components/account/WebSearchDefault';
import { useTimezone } from '@/components/contexts/TimezoneContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/NewButton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SettingRow, SettingsPanel } from '@/components/ui/settings-panel';
import { TimezoneCombobox } from '@/components/ui/timezone-combobox';
import { apiErrorMessage } from '@/lib/api-client';
import { maxLengthPolicies } from '@/lib/common';
import type { User } from '@/lib/user';
import { getPositions, updateUser } from '@/lib/user';
import { cn } from '@/lib/utils';

export function ProfilePanel({
  user,
  onUserUpdated,
}: {
  user: User;
  onUserUpdated?: () => void;
}) {
  return (
    <SettingsPanel>
      <NameRow user={user} onUserUpdated={onUserUpdated} />
      <PositionRow user={user} onUserUpdated={onUserUpdated} />
      <SettingRow label="Email" description="Your sign-in address. Contact support to change it.">
        <p className="truncate pt-1.5 text-sm text-foreground">{user.email}</p>
      </SettingRow>
      <TimezoneRow />
      <DictationLanguages />
      <WebSearchDefault />
    </SettingsPanel>
  );
}

function NameRow({
  user,
  onUserUpdated,
}: {
  user: User;
  onUserUpdated?: () => void;
}) {
  const { t } = useTranslation('common');
  const firstNameId = useId();
  const surnameId = useId();
  const [firstName, setFirstName] = useState(user.first_name);
  const [surname, setSurname] = useState(user.surname);
  const [savedFirstName, setSavedFirstName] = useState(user.first_name);
  const [savedSurname, setSavedSurname] = useState(user.surname);
  const [isSaving, setIsSaving] = useState(false);

  const trimmedFirstName = firstName.trim();
  const trimmedSurname = surname.trim();
  const isDirty = trimmedFirstName !== savedFirstName || trimmedSurname !== savedSurname;
  const isValid =
    trimmedFirstName.length > 0 &&
    trimmedFirstName.length <= maxLengthPolicies.name &&
    trimmedSurname.length <= maxLengthPolicies.name;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isDirty || !isValid || isSaving) return;
    try {
      setIsSaving(true);
      const updatedUser = await updateUser(user.guid, {
        first_name: trimmedFirstName,
        surname: trimmedSurname,
      });
      setFirstName(updatedUser.first_name);
      setSurname(updatedUser.surname);
      setSavedFirstName(updatedUser.first_name);
      setSavedSurname(updatedUser.surname);
      onUserUpdated?.();
      toast.success(t('successfully-updated'));
    } catch (error) {
      toast.error(apiErrorMessage(error) ?? t('error-updating-profile'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <SettingRow
        htmlFor={firstNameId}
        label={t('name')}
        description={t('name-appearance')}
      >
        <div className="flex items-center gap-2">
          <Input
            id={firstNameId}
            type="text"
            name="first_name"
            aria-label={t('first-name')}
            placeholder={t('your-first-name')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={maxLengthPolicies.name}
            required
          />
          <Input
            id={surnameId}
            type="text"
            name="surname"
            aria-label={t('surname')}
            placeholder={t('your-surname')}
            value={surname}
            onChange={(e) => setSurname(e.target.value)}
            maxLength={maxLengthPolicies.name}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!isDirty || !isValid || isSaving}
            loading={isSaving}
            text={t('save-changes')}
            className={cn(
              'shrink-0 transition-opacity',
              isDirty ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          />
        </div>
      </SettingRow>
    </form>
  );
}

function PositionRow({
  user,
  onUserUpdated,
}: {
  user: User;
  onUserUpdated?: () => void;
}) {
  const { t } = useTranslation('common');
  const positionId = useId();
  const [position, setPosition] = useState<string | null>(user.position ?? null);
  const [isSaving, setIsSaving] = useState(false);

  // Canonical position list is owned by the backend so the dropdown never drifts
  // from validation.
  const [positions, setPositions] = useState<string[]>([]);
  useEffect(() => {
    getPositions()
      .then(setPositions)
      .catch(() => setPositions([]));
  }, []);

  const handleChange = async (next: string) => {
    const previous = position;
    setPosition(next);
    try {
      setIsSaving(true);
      const updatedUser = await updateUser(user.guid, { position: next });
      setPosition(updatedUser.position ?? null);
      onUserUpdated?.();
      toast.success(t('successfully-updated'));
    } catch (error) {
      setPosition(previous);
      toast.error(apiErrorMessage(error) ?? t('error-updating-profile'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingRow htmlFor={positionId} label={t('position')}>
      <Select
        value={position ?? undefined}
        disabled={isSaving}
        onValueChange={(next) => {
          void handleChange(next);
        }}
      >
        <SelectTrigger id={positionId}>
          <SelectValue placeholder={t('select-a-position')} />
        </SelectTrigger>
        <SelectContent>
          {positions.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SettingRow>
  );
}

function TimezoneRow() {
  const { t } = useTranslation('common');
  const { setTimezone } = useTimezone();

  return (
    <SettingRow
      label={t('timezone-settings')}
      description={t('timezone-settings-description')}
    >
      <div className="space-y-2">
        <TimezoneCombobox align="end" />
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
          text={t('use-browser-timezone')}
          onClick={() => {
            setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
          }}
        />
      </div>
    </SettingRow>
  );
}
